import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { frDate, platformLabel, formatLabel, statusOf, STATUSES } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { toast } from "sonner";

export default function PublicationsTable({ pubs, clients = [], showClient = false, onOpen, onChanged, testPrefix = "pt" }) {
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const clientColor = (id) => clients.find((c) => c.id === id)?.color || "#999";

  const allChecked = pubs.length > 0 && selected.length === pubs.length;
  const toggleAll = (checked) => setSelected(checked ? pubs.map((p) => p.id) : []);
  const toggle = (id, checked) =>
    setSelected((s) => (checked ? [...new Set([...s, id])] : s.filter((x) => x !== id)));

  const bulkDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/publications/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} publication(s) supprimée(s)`);
      setSelected([]);
      setConfirmOpen(false);
      onChanged?.();
    } catch { toast.error("Erreur lors de la suppression"); }
    finally { setDeleting(false); }
  };

  const changeStatus = async (p, status) => {
    if (status === p.status) return;
    try {
      await api.patch(`/publications/${p.id}`, { status });
      toast.success(`Statut mis à jour : ${statusOf(status).label}`);
      onChanged?.();
    } catch { toast.error("Erreur lors du changement de statut"); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-x-auto" data-testid={`${testPrefix}-table`}>
      {selected.length > 0 && (
        <div className="flex items-center gap-3 px-5 py-3 bg-red-50/60 border-b border-red-100 animate-fade-up" data-testid={`${testPrefix}-selection-bar`}>
          <span className="text-sm font-medium text-red-800" data-testid={`${testPrefix}-selection-count`}>
            {selected.length} publication(s) sélectionnée(s)
          </span>
          <Button size="sm" data-testid={`${testPrefix}-bulk-delete-btn`} onClick={() => setConfirmOpen(true)}
            className="bg-red-600 hover:bg-red-700 text-white gap-1.5 h-8 ml-auto">
            <Trash2 className="w-3.5 h-3.5" /> Supprimer
          </Button>
          <button className="text-xs text-neutral-500 hover:text-neutral-900 transition-colors"
            onClick={() => setSelected([])} data-testid={`${testPrefix}-clear-selection`}>
            Tout désélectionner
          </button>
        </div>
      )}
      <table className="w-full text-sm min-w-[760px]">
        <thead>
          <tr className="border-b border-neutral-200 text-left text-xs text-neutral-400 uppercase tracking-wider">
            <th className="pl-5 pr-2 py-3 w-8">
              <Checkbox data-testid={`${testPrefix}-select-all`} checked={allChecked} onCheckedChange={toggleAll} aria-label="Tout sélectionner" />
            </th>
            <th className="px-3 py-3 font-medium">Publication</th>
            {showClient && <th className="px-5 py-3 font-medium">Client</th>}
            <th className="px-5 py-3 font-medium">Date</th>
            <th className="px-5 py-3 font-medium">Réseau</th>
            <th className="px-5 py-3 font-medium">Format</th>
            <th className="px-5 py-3 font-medium">Statut</th>
            {showClient && <th className="px-5 py-3 font-medium">Responsable</th>}
            {showClient && <th className="px-5 py-3 font-medium">Modifié le</th>}
          </tr>
        </thead>
        <tbody>
          {pubs.map((p) => (
            <tr key={p.id} data-testid={`${testPrefix}-row-${p.id}`} onClick={() => onOpen?.(p)}
              className={`border-b border-neutral-100 last:border-0 hover:bg-neutral-50 cursor-pointer transition-colors ${selected.includes(p.id) ? "bg-red-50/40" : ""}`}>
              <td className="pl-5 pr-2 py-3" onClick={(e) => e.stopPropagation()}>
                <Checkbox data-testid={`${testPrefix}-select-${p.id}`} checked={selected.includes(p.id)}
                  onCheckedChange={(c) => toggle(p.id, c)} aria-label={`Sélectionner ${p.title}`} />
              </td>
              <td className="px-3 py-3 font-medium max-w-[260px] truncate">{p.title}</td>
              {showClient && (
                <td className="px-5 py-3">
                  <span className="flex items-center gap-2 text-neutral-600">
                    <span className="w-2 h-2 rounded-full" style={{ background: clientColor(p.client_id) }} />
                    {clientName(p.client_id)}
                  </span>
                </td>
              )}
              <td className="px-5 py-3 text-neutral-500 whitespace-nowrap">{frDate(p.date)} · {p.time}</td>
              <td className="px-5 py-3 text-neutral-500">{platformLabel(p.platform)}</td>
              <td className="px-5 py-3">
                <span data-testid={`${testPrefix}-format-${p.id}`}
                  className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                    p.format === "story"
                      ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                      : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                  {formatLabel(p.format)}
                </span>
              </td>
              <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                <Select value={p.status} onValueChange={(v) => changeStatus(p, v)}>
                  <SelectTrigger data-testid={`${testPrefix}-status-${p.id}`}
                    className="h-auto w-auto border-0 shadow-none p-0 gap-0 hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:hidden">
                    <StatusBadge status={p.status} />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`${testPrefix}-status-option-${s.id}`}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </td>
              {showClient && <td className="px-5 py-3 text-neutral-500">{p.created_by_name || "—"}</td>}
              {showClient && <td className="px-5 py-3 text-neutral-400 text-xs">{p.updated_at ? format(parseISO(p.updated_at), "d MMM HH:mm", { locale: fr }) : "—"}</td>}
            </tr>
          ))}
        </tbody>
      </table>
      {pubs.length === 0 && <p className="px-5 py-10 text-center text-sm text-neutral-400" data-testid={`${testPrefix}-empty`}>Aucune publication.</p>}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`${testPrefix}-delete-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Supprimer {selected.length} publication(s) ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les publications sélectionnées seront supprimées des calendriers, du feed et de toutes les listes. Cette action est définitive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testPrefix}-delete-cancel`}>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testPrefix}-delete-confirm-btn`} onClick={bulkDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? "Suppression…" : "Confirmer la suppression"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
